import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Brand';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import {
  FunnelChart,
  GrowthChart,
  MrrMovementChart,
  RevenueCostChart,
} from '../components/operations/OperationsCharts';
import { useLocale } from '../i18n/LocaleProvider';
import type { SupportedLocale } from '../i18n';
import { opsErrorMessage } from '../lib/ops-api';
import {
  OPERATIONS_SETTINGS_PATH,
  RANGE_PRESETS,
  buildKpis,
  cohortShare,
  formatDecimal,
  formatInt,
  formatMoney,
  formatMonthLong,
  formatMonthShort,
  formatPercent,
  formatQuarter,
  heatBucket,
  isMonthKey,
  monthOfDate,
  pnlCell,
  pnlRows,
  pnlTotal,
  presetRange,
  quarterOf,
  rangeFunnel,
  recentQuarters,
  varianceTone,
  type Kpi,
  type MonthRange,
  type QuarterKey,
  type RangePreset,
} from '../lib/operations';
import {
  OPERATIONS_QUERY_KEYS,
  fetchFinanceReport,
  type OpsFinanceReport,
  type OpsPlanLine,
} from '../lib/operations-api';

/** Tailwind classes per cohort heat bucket — spelled out so the scanner sees them. */
const HEAT_CLASSES = [
  'bg-gray-50 text-gray-400',
  'bg-primary-50',
  'bg-primary-100',
  'bg-primary-200',
  'bg-primary-300',
];

/**
 * The operations dashboard (COO/CFO view): KPIs, a management P&L down to net income, growth and
 * MRR movements from the Shopify Partner API mirror, unit economics, plan vs. actual and install
 * cohorts.
 *
 * Every number comes computed from the API; this page picks, orders and formats. The caveats sit
 * on the page rather than in a doc, because a confidently wrong ops dashboard is worse than none:
 * accrual not cash, list-price MRR, Partner data "for analytics only", provisional FX months.
 */
/** How many quarters the quarter picker offers: the running one and the eleven before it. */
const QUARTER_CHOICES = 12;

export function OperationsPage() {
  const { t, locale } = useLocale();
  const current = monthOfDate(new Date());
  const [preset, setPreset] = useState<RangePreset>('last12');
  const [custom, setCustom] = useState<MonthRange>(() =>
    presetRange('last12', current, { from: current, to: current }),
  );
  const [quarter, setQuarter] = useState<QuarterKey>(() => quarterOf(current));
  const quarters = recentQuarters(current, QUARTER_CHOICES);
  const range = presetRange(preset, current, custom, quarter);

  const report = useQuery({
    queryKey: [...OPERATIONS_QUERY_KEYS.report, range.from, range.to],
    queryFn: () => fetchFinanceReport(range.from, range.to),
    staleTime: 60_000,
  });

  return (
    <>
      <PageHead
        eyebrow={t('operations.eyebrow')}
        title={t('operations.title')}
        lead={t('operations.lead')}
        actions={
          <Link
            to={OPERATIONS_SETTINGS_PATH}
            className="btn-secondary btn-sm gap-2"
          >
            <Icon name="settings" className="h-4 w-4" />
            {t('operations.toInputs')}
          </Link>
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <div
            className="tabs flex-wrap"
            role="tablist"
            aria-label={t('operations.range.label')}
          >
            {RANGE_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={preset === p}
                className={`tab ${preset === p ? 'tab-active' : 'tab-idle'}`}
                onClick={() => setPreset(p)}
              >
                {t(`operations.range.${p}`)}
              </button>
            ))}
          </div>
          {preset === 'quarter' && (
            <select
              className="field-input w-auto"
              aria-label={t('operations.range.quarter')}
              value={quarter}
              onChange={(e) => {
                const hit = quarters.find((q) => q === e.target.value);
                if (hit) setQuarter(hit);
              }}
            >
              {quarters.map((q, i) => (
                <option key={q} value={q}>
                  {i === 0
                    ? t('operations.range.quarterRunning', {
                        quarter: formatQuarter(q),
                      })
                    : formatQuarter(q)}
                </option>
              ))}
            </select>
          )}
          {preset === 'custom' && (
            <div className="flex items-end gap-2">
              <label className="text-sm">
                <span className="field-label">
                  {t('operations.range.from')}
                </span>
                <input
                  type="month"
                  className="field-input"
                  value={custom.from}
                  onChange={(e) =>
                    isMonthKey(e.target.value) &&
                    setCustom((c) => ({ ...c, from: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm">
                <span className="field-label">{t('operations.range.to')}</span>
                <input
                  type="month"
                  className="field-input"
                  value={custom.to}
                  onChange={(e) =>
                    isMonthKey(e.target.value) &&
                    setCustom((c) => ({ ...c, to: e.target.value }))
                  }
                />
              </label>
            </div>
          )}
        </div>
      </PageHead>
      <PageBody>
        {report.isLoading && (
          <StatusBanner tone="loading">{t('operations.loading')}</StatusBanner>
        )}
        {report.isError && (
          <StatusBanner tone="error">
            {opsErrorMessage(report.error, t('operations.loadFailed'))}
          </StatusBanner>
        )}
        {report.data && <ReportBody report={report.data} locale={locale} />}
      </PageBody>
    </>
  );
}

function ReportBody({
  report,
  locale,
}: {
  readonly report: OpsFinanceReport;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const quality = report.dataQuality;
  const noPartnerData =
    quality.eventCount === 0 && quality.transactionCount === 0;
  const funnel = rangeFunnel(report);

  return (
    <>
      {!quality.partnerConfigured && (
        <StatusBanner tone="warning">
          {t('operations.quality.notConfigured')}{' '}
          <Link
            to={`${OPERATIONS_SETTINGS_PATH}?tab=partner`}
            className="link-arrow"
          >
            {t('operations.quality.toSetup')}
          </Link>
        </StatusBanner>
      )}
      {quality.partnerConfigured && noPartnerData && (
        <StatusBanner tone="info">
          {t('operations.quality.noDataYet')}
        </StatusBanner>
      )}
      <StatusBanner tone="info">
        <ul className="list-disc pl-5 space-y-1">
          <li>{t('operations.caveats.analyticsOnly')}</li>
          <li>{t('operations.caveats.accrual')}</li>
          <li>{t('operations.caveats.listPriceMrr')}</li>
          {quality.provisionalFxMonths.length > 0 && (
            <li>
              {t('operations.caveats.provisionalFx', {
                months: quality.provisionalFxMonths
                  .map((m) => formatMonthShort(m, locale))
                  .join(', '),
              })}
            </li>
          )}
          {quality.oldestEventAt && (
            <li>
              {t('operations.caveats.depth', {
                date: new Date(quality.oldestEventAt).toLocaleDateString(
                  locale === 'de' ? 'de-DE' : 'en-GB',
                ),
              })}
            </li>
          )}
          {quality.skippedCurrencies.length > 0 && (
            <li>
              {t('operations.caveats.skippedCurrencies', {
                currencies: quality.skippedCurrencies.join(', '),
              })}
            </li>
          )}
          {!quality.billingFromSales && quality.eventCount > 0 && (
            <li>{t('operations.caveats.billingEstimated')}</li>
          )}
          {quality.unattributedEvents > 0 && (
            <li>
              {t('operations.caveats.unattributed', {
                count: formatInt(quality.unattributedEvents, locale),
              })}
            </li>
          )}
          {quality.testChargeEvents > 0 && (
            <li>
              {t('operations.caveats.testCharges', {
                count: formatInt(quality.testChargeEvents, locale),
              })}
            </li>
          )}
        </ul>
      </StatusBanner>

      <KpiGrid report={report} locale={locale} />

      <div className="grid gap-5 xl:grid-cols-2">
        <ChartCard
          title={t('operations.charts.revenueCostTitle')}
          lead={t('operations.charts.revenueCostLead')}
        >
          <RevenueCostChart months={report.pnl} />
        </ChartCard>
        <ChartCard
          title={t('operations.charts.mrrTitle')}
          lead={t('operations.charts.mrrLead')}
        >
          <MrrMovementChart months={report.saas} />
        </ChartCard>
        <ChartCard
          title={t('operations.charts.growthTitle')}
          lead={t('operations.charts.growthLead')}
        >
          <GrowthChart months={report.saas} />
        </ChartCard>
        <ChartCard
          title={
            funnel.month === null
              ? t('operations.charts.funnelTitle')
              : t('operations.charts.funnelTitleAt', {
                  month: formatMonthLong(funnel.month, locale),
                })
          }
          lead={
            funnel.month === null
              ? t('operations.charts.funnelLead')
              : t('operations.charts.funnelLeadAt')
          }
        >
          <FunnelChart
            buckets={[
              {
                label: t('operations.funnel.installed'),
                value: funnel.installed,
              },
              {
                label: t('operations.funnel.trialing'),
                value: funnel.trialing,
              },
              {
                label: t('operations.funnel.paying'),
                value: funnel.paying,
              },
              {
                label: t('operations.funnel.frozen'),
                value: funnel.frozen,
              },
              {
                label: t('operations.funnel.unconverted'),
                value: funnel.unconverted,
              },
            ]}
          />
        </ChartCard>
      </div>

      <PnlTable report={report} locale={locale} />
      <SaasTable report={report} locale={locale} />
      <UnitEconomicsTable report={report} locale={locale} />
      <PlanTable
        plan={report.plan}
        currentMonth={report.currentMonth}
        locale={locale}
      />
      <CohortTable report={report} locale={locale} />
    </>
  );
}

function ChartCard({
  title,
  lead,
  children,
}: {
  readonly title: string;
  readonly lead: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="surface p-5 space-y-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="text-xs text-gray-500">{lead}</p>
      </div>
      {children}
    </section>
  );
}

function KpiGrid({
  report,
  locale,
}: {
  readonly report: OpsFinanceReport;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const kpis = buildKpis(report, locale, {
    margin: (pct) => t('operations.kpi.margin', { pct }),
    ofInstalled: (installed) => t('operations.kpi.ofInstalled', { installed }),
    cashPositive: t('operations.kpi.cashPositive'),
    inMonth: (month) => t('operations.kpi.inMonth', { month }),
    monthToDate: (month) => t('operations.kpi.monthToDate', { month }),
    endOfMonth: (month) => t('operations.kpi.endOfMonth', { month }),
    months: (n) => t('operations.kpi.months', { n }),
    noBalance: t('operations.kpi.noBalance'),
  });
  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
      {kpis.map((kpi) => (
        <KpiTile key={kpi.id} kpi={kpi} locale={locale} />
      ))}
    </div>
  );
}

function KpiTile({
  kpi,
  locale,
}: {
  readonly kpi: Kpi;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const good =
    kpi.delta === null || kpi.delta === 0
      ? null
      : kpi.delta > 0 === kpi.goodWhenUp;
  return (
    <div className="surface p-4" title={t(`operations.kpi.${kpi.id}Hint`)}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {t(`operations.kpi.${kpi.id}`)}
      </p>
      <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
        {kpi.value}
      </p>
      <div className="mt-1 flex items-center gap-2 text-xs">
        {kpi.delta !== null && (
          <span
            className={
              good === null
                ? 'text-gray-500'
                : good
                  ? 'text-emerald-600'
                  : 'text-red-600'
            }
          >
            {kpi.delta > 0 ? '▲' : kpi.delta < 0 ? '▼' : '•'}{' '}
            {formatPercent(Math.abs(kpi.delta), locale)}
          </span>
        )}
        {kpi.sub && <span className="text-gray-500 truncate">{kpi.sub}</span>}
      </div>
    </div>
  );
}

/** A metrics × months table with a sticky label column. */
function MonthTable({
  title,
  lead,
  months,
  rows,
  showTotal,
  actions,
}: {
  readonly title: string;
  readonly lead?: string;
  readonly months: readonly {
    readonly month: string;
    readonly isPartial?: boolean;
  }[];
  readonly rows: readonly {
    readonly id: string;
    readonly label: string;
    readonly cells: readonly string[];
    readonly total?: string;
    readonly emphasis?: 'subtotal' | 'total' | 'detail' | 'margin';
    readonly negativeCells?: readonly boolean[];
  }[];
  readonly showTotal?: boolean;
  readonly actions?: ReactNode;
}) {
  const { t, locale } = useLocale();
  return (
    <section className="surface p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {lead && <p className="text-xs text-gray-500 max-w-3xl">{lead}</p>}
        </div>
        {actions}
      </div>
      <div className="overflow-x-auto">
        <table className="data-table min-w-max">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white pr-4">
                {t('operations.table.metric')}
              </th>
              {months.map((m) => (
                <th
                  key={m.month}
                  className="text-right px-2"
                  title={
                    m.isPartial ? t('operations.table.partial') : undefined
                  }
                >
                  {formatMonthShort(m.month, locale)}
                  {m.isPartial ? '*' : ''}
                </th>
              ))}
              {showTotal && (
                <th className="text-right px-2">
                  {t('operations.table.total')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const strong =
                row.emphasis === 'subtotal' || row.emphasis === 'total';
              return (
                <tr
                  key={row.id}
                  className={
                    row.emphasis === 'total'
                      ? 'bg-primary-50/60 font-semibold'
                      : row.emphasis === 'subtotal'
                        ? 'bg-gray-50 font-medium'
                        : ''
                  }
                >
                  <td
                    className={`sticky left-0 pr-4 py-1.5 ${row.emphasis === 'total' ? 'bg-primary-50' : row.emphasis === 'subtotal' ? 'bg-gray-50' : 'bg-white'} ${
                      row.emphasis === 'detail' || row.emphasis === 'margin'
                        ? 'pl-4 text-gray-500'
                        : 'text-gray-800'
                    }`}
                  >
                    {row.label}
                  </td>
                  {row.cells.map((cell, i) => (
                    <td
                      key={months[i]?.month ?? i}
                      className={`text-right px-2 py-1.5 tabular-nums ${row.negativeCells?.[i] ? 'text-red-600' : strong ? 'text-gray-900' : 'text-gray-700'} ${
                        row.emphasis === 'margin' ? 'text-xs text-gray-500' : ''
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                  {showTotal && (
                    <td className="text-right px-2 py-1.5 tabular-nums font-medium text-gray-900">
                      {row.total ?? ''}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PnlTable({
  report,
  locale,
}: {
  readonly report: OpsFinanceReport;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => pnlRows(expanded), [expanded]);
  return (
    <MonthTable
      title={t('operations.pnl.title')}
      lead={t('operations.pnl.lead')}
      months={report.pnl}
      showTotal
      actions={
        <button
          type="button"
          className="btn-neutral btn-sm"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? t('operations.pnl.collapse') : t('operations.pnl.expand')}
        </button>
      }
      rows={rows.map((row) => {
        const cells = report.pnl.map((m) => pnlCell(row, m));
        const total = pnlTotal(row, report.pnl);
        const isMargin = row.value.type === 'margin';
        const fmt = (v: number | null) =>
          isMargin ? formatPercent(v, locale) : formatMoney(v, locale);
        return {
          id: row.id,
          label:
            row.value.type === 'category'
              ? t(`operations.category.${row.value.category}`)
              : t(row.labelKey),
          cells: cells.map(fmt),
          total: fmt(total),
          negativeCells: cells.map(
            (v) =>
              !isMargin &&
              v !== null &&
              v < 0 &&
              row.kind !== 'line' &&
              row.kind !== 'category',
          ),
          emphasis:
            row.kind === 'subtotal' ||
            row.kind === 'total' ||
            row.kind === 'margin'
              ? row.kind
              : row.kind === 'detail' || row.kind === 'category'
                ? 'detail'
                : undefined,
        };
      })}
    />
  );
}

function SaasTable({
  report,
  locale,
}: {
  readonly report: OpsFinanceReport;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const s = report.saas;
  const int = (pick: (m: (typeof s)[number]) => number) =>
    s.map((m) => formatInt(pick(m), locale));
  const money = (pick: (m: (typeof s)[number]) => number) =>
    s.map((m) => formatMoney(pick(m), locale));
  const pct = (pick: (m: (typeof s)[number]) => number | null) =>
    s.map((m) => formatPercent(pick(m), locale));
  const sum = (pick: (m: (typeof s)[number]) => number) =>
    s.reduce((acc, m) => acc + pick(m), 0);
  const months = report.pnl.map((p) => ({
    month: p.month,
    isPartial: p.isPartial,
  }));
  return (
    <MonthTable
      title={t('operations.saas.title')}
      lead={t('operations.saas.lead')}
      months={months}
      showTotal
      rows={[
        {
          id: 'installs',
          label: t('operations.saas.installs'),
          cells: int((m) => m.installs),
          total: formatInt(
            sum((m) => m.installs),
            locale,
          ),
        },
        {
          id: 'reinstalls',
          label: t('operations.saas.reinstalls'),
          cells: int((m) => m.reinstalls),
          total: formatInt(
            sum((m) => m.reinstalls),
            locale,
          ),
          emphasis: 'detail',
        },
        {
          id: 'uninstalls',
          label: t('operations.saas.uninstalls'),
          cells: int((m) => m.uninstalls),
          total: formatInt(
            sum((m) => m.uninstalls),
            locale,
          ),
        },
        {
          id: 'netInstalls',
          label: t('operations.saas.netInstalls'),
          cells: int((m) => m.netInstalls),
          total: formatInt(
            sum((m) => m.netInstalls),
            locale,
          ),
          emphasis: 'subtotal',
        },
        {
          id: 'installed',
          label: t('operations.saas.installedEnd'),
          cells: int((m) => m.installedEnd),
        },
        {
          id: 'trialing',
          label: t('operations.saas.trialingEnd'),
          cells: int((m) => m.trialingEnd),
          emphasis: 'detail',
        },
        {
          id: 'frozen',
          label: t('operations.saas.frozenEnd'),
          cells: int((m) => m.frozenEnd),
          emphasis: 'detail',
        },
        {
          id: 'paying',
          label: t('operations.saas.payingEnd'),
          cells: int((m) => m.payingEnd),
          emphasis: 'subtotal',
        },
        {
          id: 'i2p',
          label: t('operations.saas.installToPaying'),
          cells: pct((m) => m.installToPaying),
          emphasis: 'margin',
        },
        {
          id: 'mrr',
          label: t('operations.saas.mrr'),
          cells: money((m) => m.mrr),
          emphasis: 'total',
        },
        {
          id: 'arr',
          label: t('operations.saas.arr'),
          cells: money((m) => m.arr),
          emphasis: 'detail',
        },
        {
          id: 'newMrr',
          label: t('operations.saas.newMrr'),
          cells: money((m) => m.newMrr),
          total: formatMoney(
            sum((m) => m.newMrr),
            locale,
          ),
        },
        {
          id: 'reactMrr',
          label: t('operations.saas.reactivationMrr'),
          cells: money((m) => m.reactivationMrr),
          total: formatMoney(
            sum((m) => m.reactivationMrr),
            locale,
          ),
        },
        {
          id: 'expMrr',
          label: t('operations.saas.expansionMrr'),
          cells: money((m) => m.expansionMrr),
          total: formatMoney(
            sum((m) => m.expansionMrr),
            locale,
          ),
        },
        {
          id: 'conMrr',
          label: t('operations.saas.contractionMrr'),
          cells: money((m) => -m.contractionMrr),
          total: formatMoney(-sum((m) => m.contractionMrr), locale),
        },
        {
          id: 'churnMrr',
          label: t('operations.saas.churnedMrr'),
          cells: money((m) => -m.churnedMrr),
          total: formatMoney(-sum((m) => m.churnedMrr), locale),
        },
        {
          id: 'netNew',
          label: t('operations.saas.netNewMrr'),
          cells: money((m) => m.netNewMrr),
          total: formatMoney(
            sum((m) => m.netNewMrr),
            locale,
          ),
          emphasis: 'subtotal',
        },
        {
          id: 'newPaying',
          label: t('operations.saas.newPaying'),
          cells: int((m) => m.newPaying),
          total: formatInt(
            sum((m) => m.newPaying),
            locale,
          ),
        },
        {
          id: 'churnedPaying',
          label: t('operations.saas.churnedPaying'),
          cells: int((m) => m.churnedPaying),
          total: formatInt(
            sum((m) => m.churnedPaying),
            locale,
          ),
        },
        {
          id: 'logoChurn',
          label: t('operations.saas.logoChurn'),
          cells: pct((m) => m.logoChurn),
          emphasis: 'margin',
        },
        {
          id: 'revChurn',
          label: t('operations.saas.revenueChurn'),
          cells: pct((m) => m.revenueChurn),
          emphasis: 'margin',
        },
        {
          id: 'nrr',
          label: t('operations.saas.netRevenueRetention'),
          cells: pct((m) => m.netRevenueRetention),
          emphasis: 'margin',
        },
        {
          id: 'trialsStarted',
          label: t('operations.saas.trialsStarted'),
          cells: int((m) => m.trialsStarted),
          total: formatInt(
            sum((m) => m.trialsStarted),
            locale,
          ),
        },
        {
          id: 'trialsConverted',
          label: t('operations.saas.trialsConverted'),
          cells: int((m) => m.trialsConverted),
          total: formatInt(
            sum((m) => m.trialsConverted),
            locale,
          ),
        },
        {
          id: 'trialsLost',
          label: t('operations.saas.trialsLost'),
          cells: int((m) => m.trialsLost),
          total: formatInt(
            sum((m) => m.trialsLost),
            locale,
          ),
        },
        {
          id: 'trialConv',
          label: t('operations.saas.trialConversion'),
          cells: pct((m) => m.trialConversion),
          emphasis: 'margin',
        },
      ]}
    />
  );
}

function UnitEconomicsTable({
  report,
  locale,
}: {
  readonly report: OpsFinanceReport;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const u = report.unitEconomics;
  const months = report.pnl.map((p) => ({
    month: p.month,
    isPartial: p.isPartial,
  }));
  return (
    <MonthTable
      title={t('operations.unit.title')}
      lead={t('operations.unit.lead')}
      months={months}
      rows={[
        {
          id: 'window',
          label: t('operations.unit.windowMonths'),
          cells: u.map((m) => String(m.windowMonths)),
        },
        {
          id: 'arpu',
          label: t('operations.unit.arpu'),
          cells: u.map((m) => formatMoney(m.arpu, locale, { cents: true })),
        },
        {
          id: 'gm',
          label: t('operations.unit.grossMargin'),
          cells: u.map((m) => formatPercent(m.grossMargin, locale)),
        },
        {
          id: 'churn',
          label: t('operations.unit.avgLogoChurn'),
          cells: u.map((m) => formatPercent(m.avgLogoChurn, locale)),
        },
        {
          id: 'ltv',
          label: t('operations.unit.ltv'),
          cells: u.map((m) => formatMoney(m.ltv, locale)),
          emphasis: 'subtotal',
        },
        {
          id: 'cac',
          label: t('operations.unit.cac'),
          cells: u.map((m) => formatMoney(m.cac, locale)),
          emphasis: 'subtotal',
        },
        {
          id: 'ratio',
          label: t('operations.unit.ltvToCac'),
          cells: u.map((m) => formatDecimal(m.ltvToCac, locale)),
          emphasis: 'total',
        },
        {
          id: 'payback',
          label: t('operations.unit.paybackMonths'),
          cells: u.map((m) => formatDecimal(m.paybackMonths, locale)),
        },
        {
          id: 'cogsShop',
          label: t('operations.unit.cogsPerPayingShop'),
          cells: u.map((m) =>
            formatMoney(m.cogsPerPayingShop, locale, { cents: true }),
          ),
        },
      ]}
    />
  );
}

function PlanTable({
  plan,
  currentMonth,
  locale,
}: {
  readonly plan: readonly OpsPlanLine[];
  readonly currentMonth: string;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  return (
    <section className="surface p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('operations.plan.title')}
          </h2>
          <p className="text-xs text-gray-500 max-w-3xl">
            {t('operations.plan.lead')}
          </p>
        </div>
        <Link
          to={`${OPERATIONS_SETTINGS_PATH}?tab=plan`}
          className="btn-neutral btn-sm"
        >
          {t('operations.plan.edit')}
        </Link>
      </div>
      {plan.length === 0 ? (
        <p className="text-sm text-gray-500">{t('operations.plan.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('operations.plan.month')}</th>
                <th>{t('operations.plan.line')}</th>
                <th className="text-right">{t('operations.plan.budget')}</th>
                <th className="text-right">{t('operations.plan.actual')}</th>
                <th className="text-right">{t('operations.plan.variance')}</th>
              </tr>
            </thead>
            <tbody>
              {plan.map((p) => {
                // The running month's actual is month-to-date against a full-month budget: its
                // variance is not a verdict yet.
                const partial = p.month === currentMonth;
                const tone = partial
                  ? 'neutral'
                  : varianceTone(p.line, p.variance);
                return (
                  <tr key={`${p.month}-${p.line}`}>
                    <td
                      className="py-1.5"
                      title={
                        partial ? t('operations.table.partial') : undefined
                      }
                    >
                      {formatMonthLong(p.month, locale)}
                      {partial ? '*' : ''}
                    </td>
                    <td>{t(`operations.planLine.${p.line}`)}</td>
                    <td className="text-right tabular-nums">
                      {formatMoney(p.budget, locale)}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatMoney(p.actual, locale)}
                    </td>
                    <td
                      className={`text-right tabular-nums font-medium ${
                        tone === 'good'
                          ? 'text-emerald-600'
                          : tone === 'bad'
                            ? 'text-red-600'
                            : 'text-gray-500'
                      }`}
                    >
                      {formatMoney(p.variance, locale)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CohortTable({
  report,
  locale,
}: {
  readonly report: OpsFinanceReport;
  readonly locale: SupportedLocale;
}) {
  const { t } = useLocale();
  const [metric, setMetric] = useState<'installed' | 'paying'>('installed');
  const width = report.cohorts.reduce(
    (w, c) => Math.max(w, c.installed.length),
    0,
  );
  return (
    <section className="surface p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('operations.cohorts.title')}
          </h2>
          <p className="text-xs text-gray-500 max-w-3xl">
            {t('operations.cohorts.lead')}
          </p>
        </div>
        <div className="tabs" role="tablist">
          {(['installed', 'paying'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={metric === m}
              className={`tab ${metric === m ? 'tab-active' : 'tab-idle'}`}
              onClick={() => setMetric(m)}
            >
              {t(`operations.cohorts.${m}`)}
            </button>
          ))}
        </div>
      </div>
      {report.cohorts.length === 0 ? (
        <p className="text-sm text-gray-500">{t('operations.cohorts.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table min-w-max">
            <thead>
              <tr>
                <th className="pr-4">{t('operations.cohorts.cohort')}</th>
                <th className="text-right px-2">
                  {t('operations.cohorts.size')}
                </th>
                {Array.from({ length: width }, (_, k) => (
                  <th key={k} className="text-right px-2">
                    {t('operations.cohorts.monthN', { n: k })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.cohorts.map((c) => (
                <tr key={c.cohort}>
                  <td className="pr-4 py-1">
                    {formatMonthShort(c.cohort, locale)}
                  </td>
                  <td className="text-right px-2 tabular-nums">
                    {formatInt(c.size, locale)}
                  </td>
                  {Array.from({ length: width }, (_, k) => {
                    const values =
                      metric === 'installed' ? c.installed : c.paying;
                    if (k >= values.length) return <td key={k} />;
                    const share = cohortShare(values[k], c.size);
                    return (
                      <td
                        key={k}
                        className={`text-right px-2 tabular-nums ${HEAT_CLASSES[heatBucket(share)]}`}
                        title={formatInt(values[k], locale)}
                      >
                        {formatPercent(share, locale)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
